import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { modalActions } from "../../../context/actions/modalActions";
import useStore from "../../../context/useStore";
import dataService from "../../../helpers/dataService";
import { MAIN_REASONS, PATH_TYPES } from "./sanctionConstants";
import {
  getSddStatus,
  getRsaType,
  getAge,
  getMaritalStatusLabel,
  fetchSanctionProposals,
  detectReiterationPersistance,
} from "./recipientHelpers";
import { fetchEpInscriptionContext } from "./fetchEpInscriptionContext";

/**
 * Hook personnalisé pour gérer la logique d'inscription d'un allocataire à une EP
 * @param {Object} recipient - L'allocataire à inscrire
 * @param {Function} refresh - Fonction de rafraîchissement après inscription
 * @returns {Object} Toutes les données et fonctions nécessaires au composant
 */
const useAddRecipientToEp = (recipient, refresh) => {
  const [state, dispatch] = useStore();
  const mActions = modalActions(state, dispatch);
  const { RSA_TYPES, MARITAL_STATUS } = state.constants.items;

  // États du formulaire
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    organizationOrigin: "",
    contradictoire: false,
    pregnancy: false,
    sanctionType: "",
    mainReason: "",
    pathType: "",
    secondaryReasons: [],
    selectedProposal: null,
    confirmNotBusinessCreation: false,
  });

  // États pour les propositions de sanction
  const [sanctionProposals, setSanctionProposals] = useState([]);
  const [detectionResult, setDetectionResult] = useState(null);
  const [showProposals, setShowProposals] = useState(false);
  const [loadingProposals, setLoadingProposals] = useState(false);
  const [proposalsError, setProposalsError] = useState(null);

  // États pour le contexte EP (historique, organismes, motifs disponibles)
  const [epHistory, setEpHistory] = useState([]);
  const [loadingContext, setLoadingContext] = useState(false);
  const [availableOrganizations, setAvailableOrganizations] = useState([]);
  const [mainReasonsConfig, setMainReasonsConfig] = useState([]);

  // Calculs dérivés des données du recipient
  const sddStatus = getSddStatus(recipient.rightsAndDuties);
  const rsaType = getRsaType(recipient.rights, RSA_TYPES);
  const age = getAge(recipient.birthdate);
  const maritalStatus = getMaritalStatusLabel(
    recipient.maritalStatus,
    MARITAL_STATUS,
  );

  // Chargement du contexte d'inscription (organisations, motifs, historique)
  useEffect(() => {
    if (recipient && recipient.id) {
      setLoadingContext(true);

      // Charger toutes les organisations de l'utilisateur
      dataService.get(`organizations/mine`, (allOrgs) => {
        // Charger le contexte EP spécifique à l'allocataire
        fetchEpInscriptionContext(recipient.id)
          .then((context) => {
            // Filtrer les organisations disponibles selon l'API
            const availableOrgs = allOrgs.filter((org) =>
              context.organismesSaisine.includes(org.id),
            );
            setAvailableOrganizations(availableOrgs);

            // Stocker la configuration des motifs principaux avec leurs types de sanction
            setMainReasonsConfig(context.motifsPrincipaux || []);

            // Stocker l'historique EP
            setEpHistory(context.allocataireHistoriqueEp || []);

            // Pré-sélectionner l'organisme si un seul disponible
            if (availableOrgs.length === 1) {
              setFormData((prev) => ({
                ...prev,
                organizationOrigin: availableOrgs[0].id,
              }));
            }

            setLoadingContext(false);
          })
          .catch((error) => {
            console.error(
              "Erreur lors de la récupération du contexte EP:",
              error,
            );
            setAvailableOrganizations(allOrgs);
            setEpHistory([]);
            setLoadingContext(false);
          });
      });
    }
  }, [recipient]);

  // Calcul des propositions de sanction
  useEffect(() => {
    if (formData.mainReason && formData.sanctionType) {
      const detection = detectReiterationPersistance(
        formData.mainReason,
        epHistory,
      );
      setDetectionResult(detection);

      setLoadingProposals(true);
      setProposalsError(null);
      setShowProposals(false);

      fetchSanctionProposals({
        userRecipient: recipient.id,
        sanctionType: formData.sanctionType,
        mainReason: formData.mainReason,
        otherReasons: formData.secondaryReasons,
        pregnancy: formData.pregnancy,
      })
        .then((result) => {
          setSanctionProposals(result.proposals);
          setDetectionResult(result.recurrenceManquement || null);
          const defaultProposal = result.proposals.find((p) => p.isDefault);
          if (defaultProposal) {
            setFormData((prev) => ({
              ...prev,
              selectedProposal: defaultProposal.id,
            }));
          }
          setShowProposals(true);
          setLoadingProposals(false);
        })
        .catch((error) => {
          console.error("Erreur lors du calcul des barèmes:", error);
          setProposalsError(
            error?.other ||
              "Aucun barème de sanction n'a été trouvé pour les critères donnés.",
          );
          setSanctionProposals([]);
          setShowProposals(false);
          setLoadingProposals(false);
        });
    }
  }, [
    formData.mainReason,
    formData.sanctionType,
    formData.secondaryReasons,
    formData.pregnancy,
    epHistory,
    recipient.id,
  ]);

  // Validation du formulaire
  const isFormValid = () => {
    const pathTypeRequired =
      formData.mainReason === MAIN_REASONS.REFUS_OFFRE_RAISONNABLE_EMPLOI.id;
    const isBlocked =
      formData.mainReason === MAIN_REASONS.REFUS_OFFRE_RAISONNABLE_EMPLOI.id &&
      formData.pathType === PATH_TYPES.SOCIAL.id;

    // Vérifier si la confirmation est requise pour les parcours Pro/Socio-Pro
    const confirmationRequired =
      formData.mainReason === MAIN_REASONS.REFUS_OFFRE_RAISONNABLE_EMPLOI.id &&
      (formData.pathType === PATH_TYPES.PROFESSIONNEL.id ||
        formData.pathType === PATH_TYPES.SOCIO_PROFESSIONNEL.id);

    return (
      formData.organizationOrigin &&
      formData.contradictoire &&
      formData.sanctionType &&
      formData.mainReason &&
      (!pathTypeRequired || formData.pathType) &&
      formData.selectedProposal &&
      !isBlocked &&
      (!confirmationRequired || formData.confirmNotBusinessCreation)
    );
  };

  // Sauvegarde de l'inscription
  const save = () => {
    if (!isFormValid()) {
      toast.error("Veuillez compléter tous les champs obligatoires");
      return;
    }

    setIsSaving(true);
    dataService.post(
      `eps/inscrire-allocataire`,
      {
        userRecipientId: recipient.id,
        organizationOrigin: formData.organizationOrigin,
        pregnancy: formData.pregnancy,
        mainReason: formData.mainReason,
        secondaryReasons: formData.secondaryReasons,
        sanctionType: formData.sanctionType,
        selectedProposal: formData.selectedProposal,
        pathType: formData.pathType || null,
      },
      () => {
        toast.success(
          "L'allocataire a bien été inscrit à la prochaine EP disponible.",
        );
        refresh();
        mActions.updateModal({
          isOpen: false,
          content: null,
        });
      },
      setErrors,
      () => setIsSaving(false),
    );
  };

  // Fermer la modal
  const closeModal = () => {
    mActions.updateModal({ isOpen: false, content: null });
  };

  return {
    // États
    formData,
    setFormData,
    isSaving,
    errors,
    sanctionProposals,
    detectionResult,
    showProposals,
    setShowProposals,
    loadingProposals,
    proposalsError,
    epHistory,
    loadingContext,
    availableOrganizations,
    mainReasonsConfig,

    // Données calculées
    sddStatus,
    rsaType,
    age,
    maritalStatus,

    // Fonctions
    save,
    isFormValid,
    closeModal,
  };
};

export default useAddRecipientToEp;
